import { useState } from 'react'

import { Box, Radio, Section } from '@factorialco/deprecated-design-system'
import { BlockedPeriodPolicyFragment } from 'generated/resources/graphql'

import { observer } from 'mobx-react'

import { useEffect } from 'modules/core/hooks/useEffect'
import i18n from 'modules/core/lib/i18n'

import { EmployeeSelectorV2 } from 'modules/employees/components/EmployeeSelectorV2'
import { EmployeeSelectorV2MultiSelectState } from 'modules/employees/components/EmployeeSelectorV2/types'
import LocationMultiselector from 'modules/timeoff/components/LocationMultiselector'
import TeamMultiselector from 'modules/timeoff/components/TeamMultiselector'
import AccessGroupStrategies from 'modules/timeoff/lib/BlockedPeriods/AccessGroupStrategies'
import type PolicyForm from 'modules/timeoff/lib/BlockedPeriods/PolicyUpdateForm'

interface Props {
  policy: BlockedPeriodPolicyFragment | undefined
  policyForm: PolicyForm
  loading: boolean
}

const Employees = ({ policy, policyForm: form, loading }: Props) => {
  const [selectedMemberIds, setMemberIds] = useState<string[]>([])

  useEffect(() => {
    if (!loading && policy?.membersAsAccesses) {
      setMemberIds(policy.membersAsAccesses.map((member) => member.id))
    }
  }, [loading, policy?.membersAsAccesses.length])

  const updateMembers = (state: EmployeeSelectorV2MultiSelectState) => {
    if (state.type === 'manual') {
      setMemberIds(state.ids)
      form.setValues({ members: state.ids })
    }
  }

  return (
    <Section
      illustration="group"
      title={i18n.t('settings.timeoff_blocked_periods.detail.employees.title')}
      description={i18n.t('settings.timeoff_blocked_periods.detail.employees.description')}
    >
      <Radio.Wrap>
        <Box gap="s16">
          <Radio
            name="everyone"
            option={{
              label: i18n.t('settings.permissions.visibility.choices.everyone'),
              value: AccessGroupStrategies.everyone,
            }}
            description={i18n.t('settings.permissions.visibility.choices.everyone_desc')}
            value={form.get('strategy').value}
            onChange={(value) => form.get('strategy').set(value)}
          />
          <Radio
            name="dynamic_group"
            option={{
              label: i18n.t('settings.permissions.visibility.choices.dynamic_group'),
              value: AccessGroupStrategies.fqlMultiselect,
            }}
            description={i18n.t('settings.permissions.visibility.choices.dynamic_group_desc')}
            value={form.get('strategy').value}
            onChange={(value) => form.get('strategy').set(value)}
          >
            <Box gap="s12">
              <TeamMultiselector teamIds={policy?.teamIds} form={form} />
              <LocationMultiselector locationIds={policy?.locationIds} form={form} />
            </Box>
          </Radio>
          <Radio
            name="manual_selection"
            option={{
              label: i18n.t('settings.permissions.visibility.choices.manual_selection'),
              value: AccessGroupStrategies.members,
            }}
            description={i18n.t('settings.permissions.visibility.choices.manual_selection_desc')}
            value={form.get('strategy').value}
            onChange={(value) => form.get('strategy').set(value)}
          >
            <EmployeeSelectorV2
              multiple
              useAccessId
              value={selectedMemberIds}
              onChange={updateMembers}
              options={{
                disableSelectAll: true,
              }}
            />
          </Radio>
        </Box>
      </Radio.Wrap>
    </Section>
  )
}

export default observer(Employees)
